import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Space } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { usePaginationFragmentHookType } from "react-relay/relay-hooks/usePaginationFragment";
import type { PaginationInterface } from "@skydio/rivet-ui";

// TODO(sam): remove this shim and this entire file
export const legacyToRivetPagination = (
  paginationProps: PaginationProps,
  resetPageNumber?: () => void
): PaginationInterface => ({
  pageNumber: paginationProps.currentPageNumber,
  totalCount: paginationProps.totalCount ?? 0,
  pageSize: paginationProps.pageSize,
  hasPreviousPage: paginationProps.hasPrevPage,
  decrementPage: paginationProps.onPrevPage,
  hasNextPage: paginationProps.hasNextPage,
  incrementPage: paginationProps.onNextPage,
  resetPageNumber: resetPageNumber ?? (() => {}),
});

export interface PaginationProps {
  currentPageNumber: number;
  totalCount?: number;
  pageSize: number;
  hasPrevPage: boolean;
  onPrevPage: () => void;
  hasNextPage: boolean;
  onNextPage: () => void;
}

const Pagination = (props: PaginationProps) => {
  const { t } = useTranslation("common");
  const low = props.currentPageNumber * props.pageSize + 1;
  const high = Math.min((props.currentPageNumber + 1) * props.pageSize, props.totalCount || 0);
  const max = props.totalCount || 0;
  return props.hasPrevPage || props.hasNextPage ? (
    <Space>
      {max > 0 && t("pagination.pageOfPage", { low, high, max })}
      <Button
        icon={<LeftOutlined />}
        disabled={!props.hasPrevPage}
        onClick={props.onPrevPage}
      ></Button>
      <Button
        icon={<RightOutlined />}
        disabled={!props.hasNextPage}
        onClick={props.onNextPage}
      ></Button>
    </Space>
  ) : (
    <></>
  );
};

export default Pagination;

interface Edge<NodeT> {
  node: NodeT | null;
}
export interface Connection<EdgeT> {
  readonly totalCount?: number;
  readonly edges: ReadonlyArray<EdgeT | null>;
}

export type ConnectionEdgeType<
  ConnectionT extends Connection<Edge<Record<string, unknown>>> | null,
> = NonNullable<NonNullable<ConnectionT>["edges"][0]>;

export type ConnectionNodeType<
  ConnectionT extends Connection<Edge<Record<string, unknown>>> | null,
> = NonNullable<ConnectionEdgeType<ConnectionT>["node"]>;

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export const getConnectionNodes = <NodeT,>(connection?: Connection<Edge<NodeT>> | null) => {
  return connection?.edges?.map(edge => edge?.node).filter(notEmpty) || [];
};

export const useConnectionNodes = <NodeT,>(connection?: Connection<Edge<NodeT>> | null) => {
  return useMemo(() => getConnectionNodes(connection), [connection]);
};

export const useNodesFromEdges = <NodeT,>(edges: Edge<NodeT>[]) => {
  return useMemo(() => edges.map(edge => edge?.node).filter(notEmpty), [edges]);
};

// Converts a relay pagination fragment query and connection into
// a set of props for the standard Pagination component and
// the current page of data results.
export const useRelayPagination = <T,>(
  paginationQuery: usePaginationFragmentHookType<any, any, any>,
  pageSize = 10,
  connection: Connection<T> | undefined | null
): [PaginationProps, T[], () => void] => {
  const [pageNumber, setPageNumber] = useState(0);

  const loadedItemCount = connection?.edges?.length ?? 0;
  const hasLoadedItemsOnNextPage = pageNumber + 1 < loadedItemCount / pageSize;
  const hasNextPage = paginationQuery.hasNext || hasLoadedItemsOnNextPage;
  const hasNextPageLoaded =
    loadedItemCount >= pageSize * (pageNumber + 2) ||
    (hasLoadedItemsOnNextPage && !paginationQuery.hasNext);

  const onNextPage = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    // If we have enough data loaded, just go to the next page else
    // fetch more data and then update the page number
    if (hasNextPageLoaded) {
      setPageNumber(pageNumber + 1);
    } else if (paginationQuery.hasNext) {
      paginationQuery.loadNext(pageSize, { onComplete: () => setPageNumber(pageNumber + 1) });
    }
  }, [hasNextPage, hasNextPageLoaded, paginationQuery, pageSize, pageNumber, setPageNumber]);

  const onPrevPage = useCallback(() => {
    setPageNumber(Math.max(pageNumber - 1, 0));
  }, [pageNumber, setPageNumber]);

  const currentPageData = useMemo(
    () =>
      (connection?.edges ?? [])
        .slice(pageSize * pageNumber, pageSize * (pageNumber + 1))
        .filter(notEmpty),
    [pageNumber, pageSize, connection]
  );

  const resetPageNumber = useCallback(() => setPageNumber(0), [setPageNumber]);

  const paginationProps: PaginationProps = {
    currentPageNumber: pageNumber,
    pageSize,
    totalCount: connection?.totalCount,
    hasPrevPage: pageNumber > 0,
    onPrevPage,
    hasNextPage,
    onNextPage,
  };

  return [paginationProps, currentPageData, resetPageNumber];
};
